/**
 * ValidationSummaryHeader Component
 *
 * Displays a summary of validation results with counts and revalidate button.
 * Shows issue count, valid employee count, and loading state.
 */

import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import type { SubmissionValidationResult } from '@/types/validation';

export interface ValidationSummaryHeaderProps {
  /** Validation result to display */
  result: SubmissionValidationResult | null;
  /** Whether validation is currently in progress */
  isValidating?: boolean;
  /** Callback when revalidate button is clicked */
  onRevalidate?: () => void;
  /** Number of unacknowledged warnings (optional) */
  unacknowledgedWarnings?: number;
}

export function ValidationSummaryHeader({
  result,
  isValidating = false,
  onRevalidate,
  unacknowledgedWarnings,
}: ValidationSummaryHeaderProps) {
  if (!result && !isValidating) {
    return null;
  }

  const totalIssues = result
    ? result.summary.total_errors + result.summary.total_warnings
    : 0;
  const hasErrors = result ? result.summary.total_errors > 0 : false;
  const hasWarnings = result ? result.summary.total_warnings > 0 : false;

  // Determine overall status color
  const getStatusColor = () => {
    if (isValidating) return 'border-blue-200 bg-blue-50';
    if (hasErrors) return 'border-red-200 bg-red-50';
    if (hasWarnings) return 'border-amber-200 bg-amber-50';
    return 'border-green-200 bg-green-50';
  };

  const getStatusIcon = () => {
    if (isValidating) {
      return <ArrowPathIcon className="h-6 w-6 text-blue-500 animate-spin" />;
    }
    if (hasErrors) {
      return <ExclamationCircleIcon className="h-6 w-6 text-red-500" />;
    }
    if (hasWarnings) {
      return <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />;
    }
    return <CheckCircleIcon className="h-6 w-6 text-green-500" />;
  };

  const getStatusText = () => {
    if (isValidating) return 'Validating...';
    if (hasErrors) return `${result?.summary.total_errors} Error(s) Found`;
    if (hasWarnings) return `${result?.summary.total_warnings} Warning(s)`;
    return 'All Valid';
  };

  return (
    <div
      className={`rounded-lg border p-4 ${getStatusColor()}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-between">
        {/* Left side: Status icon and text */}
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0">{getStatusIcon()}</div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6">
            {/* Primary status */}
            <div>
              <p className="text-sm font-medium text-gray-900">
                {getStatusText()}
              </p>
              {result && !isValidating && (
                <p className="text-xs text-gray-600">
                  {result.summary.valid_employees} of{' '}
                  {result.summary.total_employees} employees valid
                </p>
              )}
            </div>

            {/* Issue breakdown badges */}
            {result && !isValidating && totalIssues > 0 && (
              <div className="flex gap-2 mt-2 sm:mt-0">
                {hasErrors && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <ExclamationCircleIcon className="h-3.5 w-3.5" />
                    {result.summary.total_errors} error
                    {result.summary.total_errors !== 1 ? 's' : ''}
                  </span>
                )}
                {hasWarnings && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {result.summary.total_warnings} warning
                    {result.summary.total_warnings !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}

            {/* Unacknowledged warnings badge */}
            {result &&
              !isValidating &&
              unacknowledgedWarnings !== undefined &&
              unacknowledgedWarnings > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  <InformationCircleIcon className="h-3.5 w-3.5" />
                  {unacknowledgedWarnings} to acknowledge
                </span>
              )}
          </div>
        </div>

        {/* Right side: Revalidate button */}
        {onRevalidate && (
          <button
            type="button"
            onClick={onRevalidate}
            disabled={isValidating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon
              className={`h-4 w-4 ${isValidating ? 'animate-spin' : ''}`}
            />
            {isValidating ? 'Validating...' : 'Revalidate'}
          </button>
        )}
      </div>

      {/* Generation readiness message */}
      {result && !isValidating && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          {result.can_generate ? (
            result.requires_acknowledgment ? (
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <InformationCircleIcon className="h-4 w-4 flex-shrink-0" />
                Ready to generate C10 after acknowledging warnings
              </p>
            ) : (
              <p className="text-sm text-green-700 flex items-center gap-2">
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
                Ready to generate C10 file
              </p>
            )
          ) : (
            <p className="text-sm text-red-700 flex items-center gap-2">
              <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
              Fix all errors before generating C10 file
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ValidationSummaryHeader;
